import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, startSessionTimeout } from '../services/api';
import Logo from '../components/Logo';
import { webAuthnApi } from '../services/webauthn';
import useAuthStore from '../store/authStore';
import { Fingerprint } from 'lucide-react';
import { bufferToBase64URLString } from '../utils/base64Utils';

// Utility function to detect iPad devices
const isIPad = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isIPadOS = /ipad/.test(userAgent);
  const isMacWithTouch = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isSafariOnIPad = /safari/.test(userAgent) && !/chrome/.test(userAgent) && navigator.maxTouchPoints > 1;
  
  return isIPadOS || isMacWithTouch || isSafariOnIPad;
};

export default function LoginPage() {
  const navigate = useNavigate();
  const setUser = useAuthStore((state) => state.setUser);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [biometricsSupported, setBiometricsSupported] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [credentials, setCredentials] = useState(() => ({
    companyName: localStorage.getItem('lastCompanyName') || '',
    email: '',
    password: '',
  }));
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await fetch('https://auth.ts1cloud.com/auth/verifyAccess', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.message === 'Valid Session' && data.data) {
            setUser({
              companyID: data.data.companyID,
              companyName: data.data.companyName,
              username: data.data.username,
              roles: data.data.roles
            });
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkSession();
  }, [navigate, setUser]);

  useEffect(() => {
    // Check if WebAuthn is supported
    const checkBiometricsSupport = async () => {
      try {
        if (window.PublicKeyCredential) {
          const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setBiometricsSupported(available);
        }
      } catch (error) {
        console.error('Error checking biometrics support:', error);
      }
    };

    checkBiometricsSupport();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await authApi.login(credentials);
      localStorage.setItem('lastCompanyName', credentials.companyName);
      
      // Wait for session to be fully established
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force a session verification after login
      await authApi.verifyAccess();
      
      // Use replace to prevent going back to login
      navigate('/', { replace: true });
    } catch (err) {
      setError('Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  // Diagnostic function to help troubleshoot device differences
  const runDeviceDiagnostics = () => {
    console.log('=== COMPREHENSIVE DEVICE DIAGNOSTICS ===');
    console.log('Timestamp:', new Date().toISOString());
    console.log('User Agent:', navigator.userAgent);
    console.log('Platform:', navigator.platform);
    console.log('Max Touch Points:', navigator.maxTouchPoints);
    console.log('Hardware Concurrency:', navigator.hardwareConcurrency);
    console.log('Device Memory:', (navigator as any).deviceMemory);
    console.log('Connection:', (navigator as any).connection);
    console.log('WebAuthn Support:', !!window.PublicKeyCredential);
    console.log('Credentials API Support:', !!navigator.credentials);
    console.log('Is iPad (our detection):', isIPad());
    
    if (window.PublicKeyCredential) {
      console.log('PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable available:', 
        typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function');
      
      // Test platform authenticator availability
      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => {
          console.log('Platform authenticator available:', available);
        })
        .catch(error => {
          console.error('Error checking platform authenticator:', error);
        });
    }
    
    // Check for biometric authentication support
    if ('credentials' in navigator) {
      console.log('Credentials API available');
      // Try to check if we can create a credential (this will help identify if the issue is with the API itself)
      try {
        console.log('Attempting to test credential creation...');
        // This is just a test to see if the API responds
        console.log('Credentials API appears to be working');
      } catch (error) {
        console.error('Credentials API test failed:', error);
      }
    }
    
    console.log('=== END DIAGNOSTICS ===');
  };

  const handleBiometricLogin = async () => {
    setError('');
    setIsLoading(true);
    console.log('Starting biometric login...');
    console.log('Device detection - isIPad:', isIPad());

    // Comprehensive device diagnostics
    console.log('=== DEVICE DIAGNOSTICS ===');
    console.log('User Agent:', navigator.userAgent);
    console.log('Platform:', navigator.platform);
    console.log('Max Touch Points:', navigator.maxTouchPoints);
    console.log('Hardware Concurrency:', navigator.hardwareConcurrency);
    console.log('Device Memory:', (navigator as any).deviceMemory);
    console.log('Connection:', (navigator as any).connection);
    console.log('WebAuthn Support:', !!window.PublicKeyCredential);
    console.log('Credentials API Support:', !!navigator.credentials);
    
    if (window.PublicKeyCredential) {
      console.log('PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable available:', 
        typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function');
    }
    console.log('=== END DIAGNOSTICS ===');

    try {
      // For iPads, check platform authenticator availability first
      if (isIPad()) {
        try {
          const isPlatformAuthenticatorAvailable = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          console.log('Platform authenticator available on iPad:', isPlatformAuthenticatorAvailable);
          
          if (!isPlatformAuthenticatorAvailable) {
            console.error('PLATFORM AUTHENTICATOR NOT AVAILABLE - This explains why Face ID/Touch ID is not working');
            setError('Face ID or Touch ID is not available on this iPad. This could be due to:\n\n1. Biometric authentication is disabled in Settings\n2. No biometric credentials are set up\n3. Hardware issue with Face ID/Touch ID\n4. iOS version compatibility issue\n\nPlease check Settings > Face ID & Passcode (or Touch ID & Passcode) and ensure biometric authentication is enabled.');
            setIsLoading(false);
            return;
          } else {
            console.log('Platform authenticator IS available - the issue might be with credential registration or browser configuration');
          }
        } catch (error) {
          console.error('Error checking platform authenticator availability:', error);
        }
      }

      // Get authentication options from server
      const options = await webAuthnApi.getAuthenticationOptions();
      console.log('Authentication options received:', options);

      // Request the credential with iPad-specific configuration
      console.log('Requesting credential from browser...');
      const credential = await navigator.credentials.get({
        publicKey: {
          ...options,
          // Add extensions for better iPad support
          extensions: {
            appid: window.location.origin
          }
        }
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('No credentials received from browser');
      }

      console.log('Credential received from browser:', credential);

      // Prepare and verify credential
      const preparedCredential = webAuthnApi.prepareAuthenticationCredential(credential);
      console.log('Prepared credential:', preparedCredential);
      
      const verificationResponse = await webAuthnApi.verifyAuthentication(preparedCredential);
      console.log('Verification response:', verificationResponse);

      if (verificationResponse?.verified) {
        // Wait for session to be fully established
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Verify session and get user data
        const sessionResponse = await fetch('https://auth.ts1cloud.com/auth/verifyAccess', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!sessionResponse.ok) {
          console.error('Session verification failed:', await sessionResponse.text());
          throw new Error('Failed to verify session');
        }

        const sessionData = await sessionResponse.json();
        console.log('Session data:', sessionData);

        if (sessionData.message === 'Valid Session' && sessionData.data) {
          const userData = {
            companyID: sessionData.data.companyID,
            companyName: sessionData.data.companyName,
            username: sessionData.data.username,
            roles: sessionData.data.roles
          };

          // Set user data in auth store
          setUser(userData);

          // Store company name for future use
          localStorage.setItem('lastCompanyName', userData.companyName);

          // Wait longer to ensure state updates are processed
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Verify the auth store state before navigation
          const currentUser = useAuthStore.getState().user;
          console.log('Current auth store state:', currentUser);

          if (!currentUser) {
            throw new Error('User data not set in auth store');
          }

          // Make an initial data request to prime the cache
          try {
            const dataResponse = await fetch('https://s3.ts1cloud.com/service3/getLiveData', {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'x-company-id': userData.companyID.toString(),
                'x-company-name': userData.companyName,
                'Origin': window.location.origin
              }
            });
            
            if (dataResponse.ok) {
              console.log('Initial data fetch successful');
            } else {
              console.warn('Initial data fetch failed:', await dataResponse.text());
            }
          } catch (error) {
            console.warn('Initial data fetch failed:', error);
            // Continue with navigation even if initial data fetch fails
          }

          // Start the session timeout
          startSessionTimeout();

          // Navigate to home with replace to prevent going back
          navigate('/', { replace: true });
        } else {
          throw new Error('Invalid session data');
        }
      } else {
        throw new Error('Verification failed');
      }
    } catch (error: any) {
      console.error('Biometric login error:', error);
      if (error.name === 'NotAllowedError') {
        if (isIPad()) {
          setError('Biometric authentication was cancelled. On iPad, please ensure you have Face ID or Touch ID enabled and try again.');
        } else {
          setError('Biometric authentication was cancelled or timed out. Please try again.');
        }
      } else if (error.name === 'SecurityError') {
        setError('Security error during biometric login. Please ensure you are using HTTPS.');
      } else if (error.name === 'NotSupportedError') {
        if (isIPad()) {
          setError('Your iPad may not support biometric authentication. Please ensure Face ID or Touch ID is set up and enabled.');
        } else {
          setError('Your device or browser does not support biometric authentication. Please use password login.');
        }
      } else if (error.message.includes('Invalid response format')) {
        setError('Authentication failed: Invalid response format. Please try again or use password login.');
      } else if (error.name === 'InvalidStateError') {
        if (isIPad()) {
          setError('Biometric authentication failed. Please ensure you have registered biometric credentials for this account.');
        } else {
          setError('Biometric authentication failed. Please ensure you have registered biometric credentials for this account.');
        }
      } else {
        setError(`Biometric login failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-600">Checking session...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-pattern py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-lg shadow-lg">
          <div className="flex justify-center">
            <Logo className="h-12 w-auto mb-6 text-indigo-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            TS1 Monitoring System
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sign in to your account
          </p>
        </div>
        {biometricsSupported && !showPasswordForm && (
          <div className="mt-8">
            <button
              onClick={handleBiometricLogin}
              disabled={isLoading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <Fingerprint className="w-5 h-5 mr-2" />
              Sign in with Biometrics
            </button>
            {isIPad() && (
              <div className="mt-2 text-xs text-gray-600 text-center space-y-1">
                <p>On iPad: Use Face ID or Touch ID when prompted</p>
                <p>If you see "Sign in with iPhone, iPad, or Android device",</p>
                <p>select "This device" and use Face ID/Touch ID</p>
                <button
                  onClick={runDeviceDiagnostics}
                  className="mt-2 text-indigo-600 hover:text-indigo-500 underline"
                >
                  Run Device Diagnostics (check console)
                </button>
              </div>
            )}
          </div>
        )}
        {(showPasswordForm || !biometricsSupported) && <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <input
                type="text"
                required
                name="company"
                id="company-name"
                autoComplete="organization"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Company Name"
                value={credentials.companyName}
                onChange={(e) =>
                  setCredentials({ ...credentials, companyName: e.target.value })
                }
              />
            </div>
            <div>
              <input
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={credentials.email}
                onChange={(e) =>
                  setCredentials({ ...credentials, email: e.target.value })
                }
              />
            </div>
            <div>
              <input
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) =>
                  setCredentials({ ...credentials, password: e.target.value })
                }
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setShowPasswordForm(!showPasswordForm)}
            className="text-sm text-indigo-600 hover:text-indigo-500"
          >
            {showPasswordForm ? 'Use Biometric Login' : 'Use Password Login'}
          </button>
        </div>
        <footer className="mt-8 text-center text-sm text-gray-500">
          <div className="space-y-2">
            <div>
              <a href="https://thermal-systems.co.uk" target="_blank" rel="noopener noreferrer" 
                className="hover:text-indigo-600">Thermal Systems Ltd</a>
            </div>
            <div>Tel: <a href="tel:02034881030" className="hover:text-indigo-600">020 3488 1030</a></div>
            <div className="pt-2 space-x-4">
              <a href="/terms" className="hover:text-indigo-600">Terms of Service</a>
              <span>·</span>
              <a href="/privacy" className="hover:text-indigo-600">Privacy Policy</a>
              <span>·</span>
              <a href="/contact" className="hover:text-indigo-600">Contact</a>
            </div>
            <div className="text-xs">
              © {new Date().getFullYear()} Thermal Systems Ltd. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}